import { type FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

const PdpRespSharedSidebarSellerInfoAndContact = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSidebarSellerInfoAndContact" */ './PdpRespSharedSidebarSellerInfoAndContact.tsx'
        )
);

import { useSellerRating } from '../../shared/hooks/useSellerRating.ts';
import { getIsNewSeller } from '../../utils/sellerHelpers.ts';

import { type PdpRespSidebarSellerInfoAndContactLazy_viewer$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_viewer.graphql.ts';
import { type PdpRespSidebarSellerInfoAndContactLazy_item$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_item.graphql.ts';
import { type PdpRespSidebarSellerInfoAndContactLazy_user$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_user.graphql.ts';
import { type PdpRespSidebarSellerInfoAndContactLazy_seller$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_seller.graphql.ts';

import styles from './PdpRespSidebarSellerInfoAndContactLazy.module.css';

const viewerFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_viewer on Viewer {
        ...PdpRespSharedSidebarSellerInfoAndContact_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_user on User {
        ...PdpRespSharedSidebarSellerInfoAndContact_user
    }
`;
const sellerFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_seller on Seller {
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        ...PdpRespSharedSidebarSellerInfoAndContact_seller
        ...useSellerRating_seller
    }
`;
const itemFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_item on Item {
        ...PdpRespSharedSidebarSellerInfoAndContact_item
    }
`;

export const PdpRespSidebarSellerInfoAndContactLazy: FC<{
    selectedSkuId: string;
    viewer: PdpRespSidebarSellerInfoAndContactLazy_viewer$key;
    user: PdpRespSidebarSellerInfoAndContactLazy_user$key | null | undefined;
    seller: PdpRespSidebarSellerInfoAndContactLazy_seller$key;
    item: PdpRespSidebarSellerInfoAndContactLazy_item$key;
    itemQuantity?: number;
    currency: string;
    isClient: boolean;
    onNavigateToSellerSection?: () => void;
}> = ({
    selectedSkuId,
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    seller: sellerRef,
    itemQuantity,
    currency,
    isClient,
    onNavigateToSellerSection,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const item = useFragment(itemFragment, itemRef);

    const isNewSeller = getIsNewSeller(seller.monthsSinceStartDate);
    const rating = useSellerRating({ seller });
    const hasLineBreak = isNewSeller || rating === 0;

    return (
        <Suspense
            fallback={
                <div
                    className={classnames({
                        [styles.lazy]: !hasLineBreak,
                        [styles.lazyWithLineBreak]: hasLineBreak,
                    })}
                />
            }
        >
            <PdpRespSharedSidebarSellerInfoAndContact
                viewer={viewer}
                user={user}
                seller={seller}
                item={item}
                selectedSkuId={selectedSkuId}
                itemQuantity={itemQuantity}
                currency={currency}
                isClient={isClient}
                onNavigateToSellerSection={onNavigateToSellerSection}
            />
        </Suspense>
    );
};
